import { formatInr, roundMoney } from "@/lib/utils";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Normalize API row (snake_case or legacy PascalCase). */
export function normalizeTrialBalanceRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    account_id: raw.account_id ?? raw.AccountId ?? raw.party_id ?? raw.PartyId ?? null,
    account_name:
      raw.account_name ??
      raw.AccountName ??
      raw.party_name ??
      raw.PartyName ??
      raw.name ??
      "—",
    opening_debit: num(raw.opening_debit ?? raw.BalanceDebitOpening ?? raw.opening_balance_debit),
    opening_credit: num(raw.opening_credit ?? raw.BalanceCreditOpening ?? raw.opening_balance_credit),
    debit_amount: num(raw.debit_amount ?? raw.DebitAmount ?? raw.period_debit),
    credit_amount: num(raw.credit_amount ?? raw.CreditAmount ?? raw.period_credit),
    closing_debit: num(raw.closing_debit ?? raw.BalanceDebitClosing ?? raw.closing_balance_debit),
    closing_credit: num(raw.closing_credit ?? raw.BalanceCreditClosing ?? raw.closing_balance_credit),
  };
}

const TOTAL_KEYS = [
  "opening_debit",
  "opening_credit",
  "debit_amount",
  "credit_amount",
  "closing_debit",
  "closing_credit",
];

export function normalizeTrialBalanceTotals(raw) {
  if (!raw || typeof raw !== "object") {
    return Object.fromEntries(TOTAL_KEYS.map((k) => [k, 0]));
  }
  return {
    opening_debit: num(raw.opening_debit ?? raw.BalanceDebitOpening),
    opening_credit: num(raw.opening_credit ?? raw.BalanceCreditOpening),
    debit_amount: num(raw.debit_amount ?? raw.DebitAmount),
    credit_amount: num(raw.credit_amount ?? raw.CreditAmount),
    closing_debit: num(raw.closing_debit ?? raw.BalanceDebitClosing),
    closing_credit: num(raw.closing_credit ?? raw.BalanceCreditClosing),
  };
}

export function parseTrialBalanceResponse(data) {
  const payload = data && typeof data === "object" ? data : {};
  const rawRows = Array.isArray(payload.rows)
    ? payload.rows
    : Array.isArray(payload.results)
      ? payload.results
      : Array.isArray(payload)
        ? payload
        : [];
  const rows = rawRows.map(normalizeTrialBalanceRow).filter(Boolean);
  const totals =
    payload.totals != null
      ? normalizeTrialBalanceTotals(payload.totals)
      : sumTrialBalanceRows(rows);
  const meta = payload.meta && typeof payload.meta === "object" ? payload.meta : {};
  return { rows, totals, meta };
}

export function sumTrialBalanceRows(rows) {
  return rows.reduce(
    (acc, row) => {
      for (const key of TOTAL_KEYS) {
        acc[key] = roundMoney(acc[key] + num(row[key]));
      }
      return acc;
    },
    Object.fromEntries(TOTAL_KEYS.map((k) => [k, 0]))
  );
}

export function formatTrialBalanceAmount(value) {
  const n = Number(value);
  return formatInr(Number.isFinite(n) ? n : 0);
}

export function filterTrialBalanceRows(rows, quickSearch) {
  const q = String(quickSearch || "")
    .trim()
    .toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => String(r.account_name || "").toLowerCase().includes(q));
}

export async function exportTrialBalanceExcel({ rows, totals, meta }) {
  const XLSX = await import("xlsx");
  const ledgerLabel = meta?.ledger_type === "purchase" ? "Purchase" : "Sales";
  const period =
    meta?.date_from && meta?.date_to
      ? `${meta.date_from} to ${meta.date_to}`
      : meta?.date_from || meta?.date_to || "";

  const header = [
    "Account Name",
    "Opening Dr",
    "Opening Cr",
    "Debit Amount",
    "Credit Amount",
    "Closing Dr",
    "Closing Cr",
  ];

  const dataRows = rows.map((r) => [
    r.account_name,
    num(r.opening_debit),
    num(r.opening_credit),
    num(r.debit_amount),
    num(r.credit_amount),
    num(r.closing_debit),
    num(r.closing_credit),
  ]);

  const totalRow = [
    "Totals",
    num(totals.opening_debit),
    num(totals.opening_credit),
    num(totals.debit_amount),
    num(totals.credit_amount),
    num(totals.closing_debit),
    num(totals.closing_credit),
  ];

  const sheet = XLSX.utils.aoa_to_sheet([
    [`Trial Balance — ${ledgerLabel}`],
    period ? [period] : [],
    [],
    header,
    ...dataRows,
    [],
    totalRow,
  ]);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "Trial Balance");
  const from = meta?.date_from || "from";
  const to = meta?.date_to || "to";
  const type = meta?.ledger_type === "purchase" ? "purchase" : "sales";
  XLSX.writeFile(wb, `trial-balance-${type}-${from}-to-${to}.xlsx`);
}
